/**
 * DANE EDUKACYJNE — PUSZCZA NIEPOŁOMICKA (biom „Puszcza").
 *
 * Drugi biom edukacyjny po „Świecie Flag". Ten sam, niezmieniony model
 * (`EduEntry` z `domain/types/edu.ts`) obsługuje zupełnie inny temat — dokładnie
 * po to był generyczny:
 *
 *  - `facts`    → karta „Nauka" w `PetDetailPanel` (statyczne, zawsze widoczne).
 *  - `phrases`  → przyciski w `EduInteractBar`; kliknięcie robi dymek nad stworkiem.
 *                 Tu NIE są to zwroty obcojęzyczne, tylko „głos" stworka:
 *                   • zwierzę  → „Odgłos" (BEEE!, HU-HU!) + „Tropy" (jaki ślad zostawia),
 *                   • grzyb    → „Uwaga!" (JADALNY / TRUJĄCY!) + „Kiedy rosnę",
 *                   • roślina  → „Rozpoznasz mnie" + „Kiedy kwitnę".
 *                 `native` = duży tekst w dymku, `pl` = mniejsze wyjaśnienie pod spodem.
 *  - `funFact`  → czytelna karta z krótką ciekawostką.
 *  - `landmark` → karta „Naprawdę": realistyczny obrazek gatunku w naturze
 *                 (`assets/biomes/puszcza/landmarks/real_<nazwa>.webp`). W biomie flag
 *                 to był zabytek kraju; tutaj to najcenniejsza lekcja — sprite peta
 *                 jest uroczą naklejką, a dziecko musi wiedzieć, jak ten grzyb czy
 *                 zwierzę wygląda W LESIE. Karta jest fail-soft: brak pliku chowa
 *                 obrazek, podpis i tak uczy.
 *
 * ⚠ TREŚCI O GRZYBACH: pola „Uwaga!" muszą zostać BEZWZGLĘDNIE poprawne —
 *   `pet-puszcza-muchomor-sromotnikowy` (Amanita phalloides) jest śmiertelnie
 *   trujący i komunikat nigdy nie może sugerować zbierania. Zasada nadrzędna
 *   powtarzana w kilku wpisach: grzyby zbieramy TYLKO z dorosłym.
 *
 * Dodanie stworka = jeden wpis tutaj + `PetDef` w pets.config.ts + assety.
 * Bez zmian w mechanice.
 */

import type { EduEntry, EduPhrase } from '@/domain/types/edu';
import type { ID, LocalizedString } from '@/domain/types/common';

const FOREST: LocalizedString = { pl: 'Puszcza Niepołomicka', en: 'Niepołomice Forest' };
const LABEL_WHERE: LocalizedString = { pl: 'Gdzie żyje', en: 'Where it lives' };
const LABEL_KIND: LocalizedString = { pl: 'Kto to', en: 'What it is' };
const LABEL_FOOD: LocalizedString = { pl: 'Co je', en: 'What it eats' };
const LABEL_EDIBLE: LocalizedString = { pl: 'Jadalny?', en: 'Edible?' };
const LABEL_GROWS: LocalizedString = { pl: 'Gdzie rośnie', en: 'Where it grows' };
const LABEL_HEIGHT: LocalizedString = { pl: 'Jak duży', en: 'How big' };

/** Ikony przycisków — te same PNG, których używa pasek flag (bez nowych assetów). */
const ICON_VOICE = 'ui_btn_comms';
const ICON_INFO = 'ui_comm_thanks';

// ─────────────────────────────────────────────────────────────────────────────
// ZWIERZĘTA
// ─────────────────────────────────────────────────────────────────────────────

interface AnimalInput {
  petId: ID;
  /** Ssak / Ptak / Owad — trafia do faktu „Kto to". */
  kind: LocalizedString;
  food: LocalizedString;
  where: LocalizedString;
  /** Odgłos: `native` = onomatopeja krzyczana w dymku, `pl` = kiedy tak robi. */
  voice: { native: string; pl: string };
  /** Trop/ślad: `native` = krótki opis śladu, `pl` = gdzie go szukać. */
  track: { native: string; pl: string };
  funFact: LocalizedString;
  real: { imageKey: string; caption: LocalizedString };
}

function animal(input: AnimalInput): EduEntry {
  return {
    facts: [
      { label: LABEL_KIND, value: input.kind },
      { label: LABEL_FOOD, value: input.food },
      { label: LABEL_WHERE, value: input.where },
    ],
    phrases: [
      { labelPl: 'Odgłos', native: input.voice.native, pl: input.voice.pl, iconKey: ICON_VOICE },
      { labelPl: 'Tropy', native: input.track.native, pl: input.track.pl, iconKey: ICON_INFO },
    ],
    funFact: input.funFact,
    landmark: { imageKey: input.real.imageKey, caption: input.real.caption },
  };
}

const MAMMAL: LocalizedString = { pl: 'Ssak', en: 'Mammal' };
const BIRD: LocalizedString = { pl: 'Ptak', en: 'Bird' };
const INSECT: LocalizedString = { pl: 'Owad', en: 'Insect' };

const ANIMALS: AnimalInput[] = [
  {
    petId: 'pet-puszcza-sarna',
    kind: MAMMAL,
    food: { pl: 'Trawy, pączki, młode pędy', en: 'Grass, buds and shoots' },
    where: { pl: 'Skraje lasu i polany', en: 'Forest edges and glades' },
    voice: { native: 'BEK! BEK!', pl: 'sarna szczeka jak mały piesek, gdy się przestraszy' },
    track: { native: 'Dwie małe racice', pl: 'ślad wielkości dwóch paznokci, w błocie przy ścieżce' },
    funFact: { pl: 'Sarna nie jest żoną jelenia! To zupełnie inne zwierzę — dużo mniejsze, wielkości dużego psa.', en: 'A roe deer is not a red deer’s wife — it is a different, much smaller animal.' },
    real: { imageKey: 'real_sarna', caption: { pl: 'Sarna na leśnej polanie o świcie', en: 'Roe deer in a forest glade at dawn' } },
  },
  {
    petId: 'pet-puszcza-jelen',
    kind: MAMMAL,
    food: { pl: 'Trawy, żołędzie, kora', en: 'Grass, acorns, bark' },
    where: { pl: 'Głąb puszczy, stare dąbrowy', en: 'Deep forest, old oak stands' },
    voice: { native: 'UUUOOOOH!', pl: 'ryk byka jesienią — to rykowisko, słychać go na kilometr' },
    track: { native: 'Duże racice', pl: 'ślad wielkości dłoni dziecka' },
    funFact: { pl: 'Jeleń co roku zrzuca poroże i zapuszcza nowe, większe. Zrzucone poroże można znaleźć w lesie wiosną.', en: 'A red deer sheds its antlers every year and grows bigger ones.' },
    real: { imageKey: 'real_jelen', caption: { pl: 'Byk jelenia szlachetnego z porożem', en: 'Red deer stag with antlers' } },
  },
  {
    petId: 'pet-puszcza-dzik',
    kind: MAMMAL,
    food: { pl: 'Żołędzie, korzenie, robaki', en: 'Acorns, roots, grubs' },
    where: { pl: 'Wilgotne olsy i gąszcze', en: 'Wet alder woods and thickets' },
    voice: { native: 'CHRUM! CHRR!', pl: 'chrumka i parska, ryjąc ściółkę' },
    track: { native: 'Racice + dwie kropki', pl: 'z tyłu odbijają się dodatkowo dwie szpilki' },
    funFact: { pl: 'Dziki ryją ziemię ryjem i przy okazji sadzą las — roznoszą żołędzie, z których wyrastają dęby.', en: 'Wild boars plough the soil and accidentally plant the forest.' },
    real: { imageKey: 'real_dzik', caption: { pl: 'Dzik z warchlakami w podszycie', en: 'Wild boar with piglets' } },
  },
  {
    petId: 'pet-puszcza-los',
    kind: MAMMAL,
    food: { pl: 'Gałązki wierzby, rośliny wodne', en: 'Willow twigs, water plants' },
    where: { pl: 'Bagna i mokradła puszczy', en: 'Marshes and wetlands' },
    voice: { native: 'MUUH-OOH', pl: 'głębokie chrapliwe muczenie — łoś woła rzadko' },
    track: { native: 'Ogromne racice', pl: 'największy trop w polskim lesie, dłuższy niż stopa dorosłego' },
    funFact: { pl: 'Łoś to największe zwierzę polskich lasów — w kłębie wyższy od konia i potrafi przejść przez bagno, w którym utknąłby traktor.', en: 'The moose is the largest animal of Polish forests.' },
    real: { imageKey: 'real_los', caption: { pl: 'Łoś brodzący po bagnie', en: 'Moose wading through a marsh' } },
  },
  {
    petId: 'pet-puszcza-daniel',
    kind: MAMMAL,
    food: { pl: 'Trawy, liście, żołędzie', en: 'Grass, leaves, acorns' },
    where: { pl: 'Widne lasy i polany', en: 'Open woodland and glades' },
    voice: { native: 'CHROO! CHROO!', pl: 'chrapliwe chrząkanie w czasie jesiennych godów' },
    track: { native: 'Racice mniejsze niż jelenia', pl: 'trop pomiędzy sarnim a jelenim' },
    funFact: { pl: 'Daniela poznasz po białych cętkach i łopatowatym porożu — płaskim jak wiosło, nie gałęzistym jak u jelenia.', en: 'Fallow deer have white spots and flat, shovel-shaped antlers.' },
    real: { imageKey: 'real_daniel', caption: { pl: 'Daniel z łopatowatym porożem', en: 'Fallow deer with palmate antlers' } },
  },
  {
    petId: 'pet-puszcza-lis',
    kind: MAMMAL,
    food: { pl: 'Myszy, ptaki, owoce', en: 'Mice, birds, fruit' },
    where: { pl: 'Nory na suchych skarpach', en: 'Burrows on dry slopes' },
    voice: { native: 'KHAU! KHAU!', pl: 'ostry, skrzekliwy szczek — najgłośniej zimą' },
    track: { native: 'Ślad jak małego psa', pl: 'ale lis stawia łapy w jednej linii, jak po sznurku' },
    funFact: { pl: 'Lis słyszy mysz pod śniegiem i skacze w górę, żeby przebić się przez zaspę pyskiem naprzód.', en: 'A fox hears a mouse under the snow and dives headfirst into the drift.' },
    real: { imageKey: 'real_lis', caption: { pl: 'Rudy lis na leśnej ścieżce', en: 'Red fox on a forest path' } },
  },
  {
    petId: 'pet-puszcza-borsuk',
    kind: MAMMAL,
    food: { pl: 'Dżdżownice, owady, korzonki', en: 'Earthworms, insects, roots' },
    where: { pl: 'Głębokie nory zwane borsuczymi', en: 'Deep setts' },
    voice: { native: 'MRUK... PRRR', pl: 'mruczy i prycha przy wejściu do nory, nocą' },
    track: { native: 'Pięć palców z pazurami', pl: 'szeroki, „niedźwiedzi" ślad — borsuk ma 5 palców' },
    funFact: { pl: 'Borsucze nory potrafią mieć kilkadziesiąt metrów korytarzy i są używane przez wiele pokoleń borsuków.', en: 'Badger setts can be dozens of metres long and used for generations.' },
    real: { imageKey: 'real_borsuk', caption: { pl: 'Borsuk wychodzący z nory o zmierzchu', en: 'Badger leaving its sett at dusk' } },
  },
  {
    petId: 'pet-puszcza-kuna',
    kind: MAMMAL,
    food: { pl: 'Wiewiórki, ptaki, jagody', en: 'Squirrels, birds, berries' },
    where: { pl: 'Dziuple wysoko w starych drzewach', en: 'Tree holes high up' },
    voice: { native: 'CZK-CZK-CZK!', pl: 'szybkie cmokanie i warkot, gdy się złości' },
    track: { native: 'Dwa ślady obok siebie', pl: 'kuna skacze, więc zostawia pary śladów' },
    funFact: { pl: 'Kuna leśna ma na piersi żółtą lub pomarańczową plamę zwaną śliniakiem — każda kuna ma ją trochę inną.', en: 'Every pine marten has a slightly different yellow bib.' },
    real: { imageKey: 'real_kuna', caption: { pl: 'Kuna leśna na konarze', en: 'Pine marten on a branch' } },
  },
  {
    petId: 'pet-puszcza-bobr',
    kind: MAMMAL,
    food: { pl: 'Kora, gałązki, rośliny wodne', en: 'Bark, twigs, water plants' },
    where: { pl: 'Rzeki i rozlewiska puszczy', en: 'Rivers and backwaters' },
    voice: { native: 'PLASK!', pl: 'to nie głos — bóbr uderza ogonem w wodę, żeby ostrzec rodzinę' },
    track: { native: 'Ścięte drzewo jak ołówek', pl: 'pniak zaostrzony zębami — najpewniejszy znak, że bóbr tu jest' },
    funFact: { pl: 'Bóbr buduje tamy i robi z lasu mokradła, dzięki czemu mają gdzie mieszkać żaby, ptaki i ważki.', en: 'Beavers build dams and create wetlands for many other animals.' },
    real: { imageKey: 'real_bobr', caption: { pl: 'Bóbr europejski przy swojej tamie', en: 'European beaver at its dam' } },
  },
  {
    petId: 'pet-puszcza-wydra',
    kind: MAMMAL,
    food: { pl: 'Ryby, raki, żaby', en: 'Fish, crayfish, frogs' },
    where: { pl: 'Czyste rzeki i starorzecza', en: 'Clean rivers and oxbows' },
    voice: { native: 'FIU! FIU!', pl: 'wysoki gwizd, którym wydry wołają się nad wodą' },
    track: { native: 'Łapa z błoną pływną', pl: 'ślad z błoną między palcami, zawsze przy wodzie' },
    funFact: { pl: 'Wydra potrafi zamknąć uszy i nos pod wodą i wytrzymać bez oddechu kilka minut.', en: 'An otter can close its ears and nose underwater.' },
    real: { imageKey: 'real_wydra', caption: { pl: 'Wydra na brzegu rzeki', en: 'Otter on a riverbank' } },
  },
  {
    petId: 'pet-puszcza-wilk',
    kind: MAMMAL,
    food: { pl: 'Sarny, jelenie, dziki', en: 'Roe deer, red deer, boar' },
    where: { pl: 'Najspokojniejsze ostępy puszczy', en: 'The quietest parts of the forest' },
    voice: { native: 'AUUUUUUU!', pl: 'wycie — tak wataha mówi sobie „jesteśmy tutaj"' },
    track: { native: 'Duży ślad psa w linii', pl: 'wilki idą jeden za drugim, stopa w stopę' },
    funFact: { pl: 'Wilki wróciły do Puszczy Niepołomickiej po wielu latach nieobecności. Są pod ścisłą ochroną i unikają ludzi.', en: 'Wolves have returned to Niepołomice Forest and are strictly protected.' },
    real: { imageKey: 'real_wilk', caption: { pl: 'Wilk szary w zimowym lesie', en: 'Grey wolf in a winter forest' } },
  },
  {
    petId: 'pet-puszcza-zajac',
    kind: MAMMAL,
    food: { pl: 'Trawy, zioła, kora zimą', en: 'Grass, herbs, bark in winter' },
    where: { pl: 'Pola i polany przy puszczy', en: 'Fields and glades' },
    voice: { native: '(cicho)', pl: 'zając prawie nigdy nie wydaje dźwięku — krzyczy tylko w niebezpieczeństwie' },
    track: { native: 'Długie tylne łapy z przodu', pl: 'w skoku tylne łapy lądują PRZED przednimi' },
    funFact: { pl: 'Zając nie kopie nory — śpi w płytkim dołku zwanym kotliną i ucieka zygzakiem, biegnąc do 70 km/h.', en: 'Hares do not dig burrows; they rest in a shallow form.' },
    real: { imageKey: 'real_zajac', caption: { pl: 'Zając szarak przyczajony w trawie', en: 'Brown hare crouching in grass' } },
  },
  {
    petId: 'pet-puszcza-wiewiorka',
    kind: MAMMAL,
    food: { pl: 'Orzechy, nasiona szyszek, żołędzie', en: 'Nuts, cone seeds, acorns' },
    where: { pl: 'Korony drzew, gniazdo zwane gaździną', en: 'Tree canopy' },
    voice: { native: 'CZK-CZK-CZK!', pl: 'cmoka i tupie łapkami, gdy jest zła' },
    track: { native: 'Ogryziona szyszka', pl: 'szyszka obgryziona do głąba = tu jadła wiewiórka' },
    funFact: { pl: 'Wiewiórka chowa orzechy w wielu miejscach i o części zapomina — z tych zapomnianych wyrastają nowe drzewa.', en: 'Squirrels forget some of their buried nuts, and new trees grow from them.' },
    real: { imageKey: 'real_wiewiorka', caption: { pl: 'Wiewiórka ruda z orzechem', en: 'Red squirrel with a nut' } },
  },
  {
    petId: 'pet-puszcza-jez',
    kind: MAMMAL,
    food: { pl: 'Ślimaki, owady, dżdżownice', en: 'Snails, insects, worms' },
    where: { pl: 'Stosy liści i chrustu', en: 'Leaf and brush piles' },
    voice: { native: 'PFF! PRYCH!', pl: 'prycha i sapie, gdy się zwija w kulkę' },
    track: { native: 'Maleńkie rączki', pl: 'ślad podobny do dłoni z pięcioma palcami' },
    funFact: { pl: 'Jeż ma około 6 tysięcy kolców i przesypia całą zimę. Nigdy nie daj mu mleka — jeże po nim chorują.', en: 'A hedgehog has around 6,000 spines and hibernates all winter.' },
    real: { imageKey: 'real_jez', caption: { pl: 'Jeż wśród jesiennych liści', en: 'Hedgehog among autumn leaves' } },
  },
  {
    petId: 'pet-puszcza-puszczyk',
    kind: BIRD,
    food: { pl: 'Myszy, norniki, duże owady', en: 'Mice, voles, large insects' },
    where: { pl: 'Dziuple starych dębów', en: 'Holes in old oaks' },
    voice: { native: 'HU-HUUUU-HU!', pl: 'to właśnie ta sowa, którą słychać w lesie po zmroku' },
    track: { native: 'Wypluwka', pl: 'szara kulka z kostek i sierści pod drzewem — resztki obiadu sowy' },
    funFact: { pl: 'Pióra puszczyka mają miękkie brzegi, dzięki czemu leci ZUPEŁNIE bezgłośnie — mysz go nie usłyszy.', en: 'A tawny owl’s soft-edged feathers make its flight completely silent.' },
    real: { imageKey: 'real_puszczyk', caption: { pl: 'Puszczyk zwyczajny w dziupli', en: 'Tawny owl in a tree hole' } },
  },
  {
    petId: 'pet-puszcza-dzieciol',
    kind: BIRD,
    food: { pl: 'Larwy chrząszczy, mrówki', en: 'Beetle larvae, ants' },
    where: { pl: 'Stare drzewa, sam kuje dziuple', en: 'Old trees; it carves its own holes' },
    voice: { native: 'TU-TU-TU-TU-TU!', pl: 'bębnienie dziobem w suchy konar — słychać na pół lasu' },
    track: { native: 'Podłużna dziupla', pl: 'otwór wysoko w pniu, owalny jak jajko' },
    funFact: { pl: 'Dzięcioł uderza dziobem nawet 20 razy na sekundę, a mózg chroni mu naturalny „kask" z kości.', en: 'A woodpecker strikes up to 20 times a second and has a built-in helmet of bone.' },
    real: { imageKey: 'real_dzieciol', caption: { pl: 'Dzięcioł czarny na pniu', en: 'Black woodpecker on a trunk' } },
  },
  {
    petId: 'pet-puszcza-bielik',
    kind: BIRD,
    food: { pl: 'Ryby, ptaki wodne', en: 'Fish, water birds' },
    where: { pl: 'Gniazda na najwyższych sosnach', en: 'Nests in the tallest pines' },
    voice: { native: 'KLI-KLI-KLI!', pl: 'urywany, wysoki krzyk krążącego bielika' },
    track: { native: 'Gniazdo wielkie jak wanna', pl: 'ta sama para dokłada do niego gałęzie przez wiele lat' },
    funFact: { pl: 'Bielik to nasz największy ptak drapieżny — rozpiętość skrzydeł do 2,5 metra. To on jest na polskim godle.', en: 'The white-tailed eagle is Poland’s largest bird of prey, with a 2.5 m wingspan.' },
    real: { imageKey: 'real_bielik', caption: { pl: 'Bielik z rozpostartymi skrzydłami', en: 'White-tailed eagle in flight' } },
  },
  {
    petId: 'pet-puszcza-bocian-czarny',
    kind: BIRD,
    food: { pl: 'Ryby, żaby, owady wodne', en: 'Fish, frogs, water insects' },
    where: { pl: 'Ciche śródleśne strumienie', en: 'Quiet forest streams' },
    voice: { native: '(cichutko)', pl: 'w przeciwieństwie do białego bociana prawie nie klekocze' },
    track: { native: 'Długie ślady palców w mule', pl: 'trzy palce do przodu, przy samej wodzie' },
    funFact: { pl: 'Bocian czarny to leśny, bardzo płochliwy kuzyn bociana białego — zamiast na dachu gniazduje wysoko w puszczy.', en: 'The black stork is the shy forest cousin of the white stork.' },
    real: { imageKey: 'real_bocian_czarny', caption: { pl: 'Bocian czarny nad leśnym strumieniem', en: 'Black stork by a forest stream' } },
  },
  {
    petId: 'pet-puszcza-jelonek-rogacz',
    kind: INSECT,
    food: { pl: 'Sok wyciekający z dębów', en: 'Sap oozing from oaks' },
    where: { pl: 'Stare dęby i próchniejące pnie', en: 'Old oaks and rotting stumps' },
    voice: { native: 'BZZZ-KLAP', pl: 'ciężki, głośny lot o zmierzchu — brzmi jak mały helikopter' },
    track: { native: 'Próchno z otworami', pl: 'larwy żyją w murszejącym drewnie nawet 5 lat' },
    funFact: { pl: 'Jelonek rogacz to największy chrząszcz Europy. Jego „rogi" to tak naprawdę ogromne żuchwy — i tylko samce je mają.', en: 'The stag beetle is Europe’s largest beetle; its “antlers” are jaws.' },
    real: { imageKey: 'real_jelonek_rogacz', caption: { pl: 'Jelonek rogacz na korze dębu', en: 'Stag beetle on oak bark' } },
  },
  {
    petId: 'pet-puszcza-koziorog',
    kind: INSECT,
    food: { pl: 'Drewno starych dębów', en: 'Wood of old oaks' },
    where: { pl: 'Nasłonecznione, wiekowe dęby', en: 'Sunlit ancient oaks' },
    voice: { native: 'SKRZYP-SKRZYP', pl: 'skrzypi przedpleczem, gdy go coś zaniepokoi' },
    track: { native: 'Owalne otwory w korze', pl: 'wielkości monety — tędy wyszedł dorosły chrząszcz' },
    funFact: { pl: 'Kozioróg dębosz ma czułki dłuższe od całego ciała i jest ściśle chroniony — żyje tylko tam, gdzie zostały bardzo stare dęby.', en: 'The great capricorn beetle has antennae longer than its body and is strictly protected.' },
    real: { imageKey: 'real_koziorog', caption: { pl: 'Kozioróg dębosz na starym dębie', en: 'Great capricorn beetle on an old oak' } },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// ŻUBR — headline biomu (huge-only). Osobny wpis, bo ma bogatszą treść.
// ─────────────────────────────────────────────────────────────────────────────

const ZUBR: EduEntry = {
  facts: [
    { label: LABEL_KIND, value: MAMMAL },
    { label: LABEL_FOOD, value: { pl: 'Trawy, kora, gałązki — do 60 kg dziennie', en: 'Grass, bark, twigs — up to 60 kg a day' } },
    { label: LABEL_WHERE, value: { pl: 'Ośrodek Hodowli Żubrów w Niepołomicach', en: 'Bison breeding centre in Niepołomice' } },
  ],
  phrases: [
    { labelPl: 'Odgłos', native: 'MRUUUK! CHRR!', pl: 'żubr chrząka i pomrukuje — ryczy bardzo rzadko', iconKey: ICON_VOICE },
    { labelPl: 'Tropy', native: 'Racice jak talerz', pl: 'największy ślad kopytny w Polsce', iconKey: ICON_INFO },
  ],
  funFact: {
    pl: 'Żubr to najcięższe zwierzę lądowe Europy — dorosły byk waży tyle, co mały samochód. W Niepołomicach hoduje się żubry od 1939 roku i stąd trafiają do lasów całej Polski.',
    en: 'The European bison is Europe’s heaviest land animal; Niepołomice has been breeding them since 1939.',
  },
  landmark: {
    imageKey: 'real_zubr',
    caption: { pl: 'Żubr w Ośrodku Hodowli Żubrów w Niepołomicach', en: 'European bison at the Niepołomice breeding centre' },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// GRZYBY
// ─────────────────────────────────────────────────────────────────────────────

interface MushroomInput {
  petId: ID;
  /** `true` = jadalny, `false` = trujący. Steruje treścią karty „Uwaga!". */
  edible: boolean;
  /** Krótki werdykt w dymku, np. 'JADALNY!' albo 'TRUJĄCY!'. */
  warning: { native: string; pl: string };
  season: { native: string; pl: string };
  grows: LocalizedString;
  edibleLabel: LocalizedString;
  funFact: LocalizedString;
  real: { imageKey: string; caption: LocalizedString };
}

function mushroom(input: MushroomInput): EduEntry {
  const phrases: EduPhrase[] = [
    { labelPl: 'Uwaga!', native: input.warning.native, pl: input.warning.pl, iconKey: ICON_VOICE },
    { labelPl: 'Kiedy rosnę', native: input.season.native, pl: input.season.pl, iconKey: ICON_INFO },
  ];
  return {
    facts: [
      { label: LABEL_EDIBLE, value: input.edibleLabel },
      { label: LABEL_GROWS, value: input.grows },
      { label: LABEL_KIND, value: { pl: 'Grzyb', en: 'Mushroom' } },
    ],
    phrases,
    funFact: input.funFact,
    landmark: { imageKey: input.real.imageKey, caption: input.real.caption },
  };
}

const EDIBLE_YES: LocalizedString = { pl: 'Tak — ale tylko z dorosłym', en: 'Yes — only with an adult' };
const EDIBLE_NO: LocalizedString = { pl: 'NIE — trujący!', en: 'NO — poisonous!' };
const EDIBLE_DEADLY: LocalizedString = { pl: 'NIE — śmiertelnie trujący!', en: 'NO — deadly poisonous!' };

const MUSHROOMS: MushroomInput[] = [
  {
    petId: 'pet-puszcza-podgrzybek',
    edible: true,
    edibleLabel: EDIBLE_YES,
    warning: { native: 'JADALNY!', pl: 'ale grzyby zbieramy ZAWSZE z dorosłym' },
    season: { native: 'Lipiec–Październik', pl: 'najwięcej po ciepłym deszczu' },
    grows: { pl: 'Bory sosnowe i lasy mieszane', en: 'Pine and mixed woods' },
    funFact: { pl: 'Podgrzybek ma pod kapeluszem gąbkę zamiast blaszek, a gdy ją naciśniesz, sinieje na niebiesko. To normalne!', en: 'Bay bolete has a sponge under its cap that turns blue when pressed.' },
    real: { imageKey: 'real_podgrzybek', caption: { pl: 'Podgrzybek brunatny w mchu', en: 'Bay bolete in moss' } },
  },
  {
    petId: 'pet-puszcza-maslak',
    edible: true,
    edibleLabel: EDIBLE_YES,
    warning: { native: 'JADALNY!', pl: 'kapelusz trzeba obrać ze śliskiej skórki' },
    season: { native: 'Sierpień–Listopad', pl: 'rośnie gromadnie pod sosnami' },
    grows: { pl: 'Młode sośniny', en: 'Young pine stands' },
    funFact: { pl: 'Maślak ma śliski, jakby posmarowany masłem kapelusz — stąd nazwa. Po deszczu klei się do palców.', en: 'The slippery jack has a buttery, sticky cap — hence its Polish name.' },
    real: { imageKey: 'real_maslak', caption: { pl: 'Maślak zwyczajny pod sosną', en: 'Slippery jack under a pine' } },
  },
  {
    petId: 'pet-puszcza-opienka',
    edible: true,
    edibleLabel: { pl: 'Tak — po ugotowaniu, z dorosłym', en: 'Yes — cooked, with an adult' },
    warning: { native: 'TYLKO UGOTOWANA!', pl: 'surowa opieńka szkodzi — musi się gotować' },
    season: { native: 'Wrzesień–Listopad', pl: 'całe kępy na pniakach' },
    grows: { pl: 'Pniaki i chore drzewa', en: 'Stumps and ailing trees' },
    funFact: { pl: 'Opieńka rośnie wielkimi kępami na pniakach, a jej podziemna grzybnia potrafi świecić w ciemności bladozielonym światłem.', en: 'Honey fungus grows in clumps and its mycelium can glow in the dark.' },
    real: { imageKey: 'real_opienka', caption: { pl: 'Kępa opieniek miodowych na pniaku', en: 'Honey fungus clump on a stump' } },
  },
  {
    petId: 'pet-puszcza-purchawka',
    edible: true,
    edibleLabel: { pl: 'Tak — tylko gdy młoda i biała', en: 'Yes — only when young and white' },
    warning: { native: 'TYLKO BIAŁA W ŚRODKU!', pl: 'brązowa purchawka to już tylko kula dymu' },
    season: { native: 'Lipiec–Październik', pl: 'na ściółce i trawiastych duktach' },
    grows: { pl: 'Ściółka leśna i dukty', en: 'Forest floor and tracks' },
    funFact: { pl: 'Naciśnij dojrzałą purchawkę, a wystrzeli chmurką brązowego „dymu" — to miliony zarodników, z których wyrosną nowe grzyby.', en: 'A ripe puffball shoots a cloud of spores when squeezed.' },
    real: { imageKey: 'real_purchawka', caption: { pl: 'Purchawka chropowata na ściółce', en: 'Common puffball on the forest floor' } },
  },
  {
    petId: 'pet-puszcza-muchomor-czerwony',
    edible: false,
    edibleLabel: EDIBLE_NO,
    warning: { native: 'TRUJĄCY!', pl: 'nie dotykaj i nigdy nie wkładaj do koszyka' },
    season: { native: 'Sierpień–Październik', pl: 'pod brzozami i świerkami' },
    grows: { pl: 'Pod brzozami i świerkami', en: 'Under birches and spruces' },
    funFact: { pl: 'Czerwony kapelusz w białe kropki to najsłynniejszy grzyb z bajek — i jeden z najbardziej trujących. Wolno go tylko oglądać.', en: 'The red-and-white fly agaric is the fairy-tale mushroom — and poisonous.' },
    real: { imageKey: 'real_muchomor_czerwony', caption: { pl: 'Muchomor czerwony — tylko oglądać!', en: 'Fly agaric — look, don’t touch' } },
  },
  {
    petId: 'pet-puszcza-kurka',
    edible: true,
    edibleLabel: EDIBLE_YES,
    warning: { native: 'JADALNA!', pl: 'pachnie morelą i nigdy nie bywa robaczywa' },
    season: { native: 'Czerwiec–Październik', pl: 'całymi rodzinkami w mchu' },
    grows: { pl: 'Mszyste lasy mieszane', en: 'Mossy mixed woods' },
    funFact: { pl: 'Kurki rosną gromadkami i prawie nigdy nie mają robaków. Pod kapeluszem mają fałdki, nie blaszki.', en: 'Chanterelles grow in groups and are almost never wormy.' },
    real: { imageKey: 'real_kurka', caption: { pl: 'Kurki (pieprznik jadalny) w mchu', en: 'Chanterelles in moss' } },
  },
  {
    petId: 'pet-puszcza-kozlarz',
    edible: true,
    edibleLabel: EDIBLE_YES,
    warning: { native: 'JADALNY!', pl: 'szuka się go tam, gdzie rosną brzozy' },
    season: { native: 'Czerwiec–Październik', pl: 'w brzezinach po deszczu' },
    grows: { pl: 'Brzeziny i skraje lasu', en: 'Birch woods and edges' },
    funFact: { pl: 'Koźlarz przyjaźni się z brzozą — ich korzenie i grzybnia wymieniają się jedzeniem. Dlatego pod brzozą szukaj koźlarza.', en: 'Birch bolete lives in partnership with birch trees.' },
    real: { imageKey: 'real_kozlarz', caption: { pl: 'Koźlarz babka pod brzozą', en: 'Birch bolete under a birch' } },
  },
  {
    petId: 'pet-puszcza-kania',
    edible: true,
    edibleLabel: { pl: 'Tak — ale łatwo pomylić, z dorosłym!', en: 'Yes — but easily confused; adult only' },
    warning: { native: 'JADALNA, ALE UWAŻAJ!', pl: 'młoda kania bywa mylona z muchomorem — pokaż dorosłemu' },
    season: { native: 'Lipiec–Październik', pl: 'na polanach i skrajach lasu' },
    grows: { pl: 'Polany i widne skraje', en: 'Glades and open edges' },
    funFact: { pl: 'Kapelusz dorosłej kani bywa wielkości talerza, a na trzonie ma ruchomy pierścień, który da się przesuwać w górę i w dół.', en: 'A parasol mushroom’s cap can be plate-sized, with a movable ring on the stem.' },
    real: { imageKey: 'real_kania', caption: { pl: 'Czubajka kania na polanie', en: 'Parasol mushroom in a glade' } },
  },
  {
    petId: 'pet-puszcza-borowik',
    edible: true,
    edibleLabel: EDIBLE_YES,
    warning: { native: 'KRÓL GRZYBÓW!', pl: 'jadalny — najbardziej poszukiwany grzyb w puszczy' },
    season: { native: 'Lipiec–Październik', pl: 'chowa się pod liśćmi, trzeba patrzeć pod nogi' },
    grows: { pl: 'Stare dąbrowy i bory', en: 'Old oak and pine stands' },
    funFact: { pl: 'Borowika nazywa się prawdziwkiem i królem grzybów. Ma gruby, beczułkowaty trzon i biały, nie sinający miąższ.', en: 'The porcini is called the king of mushrooms in Poland.' },
    real: { imageKey: 'real_borowik', caption: { pl: 'Borowik szlachetny (prawdziwek)', en: 'Porcini (king bolete)' } },
  },
  {
    petId: 'pet-puszcza-muchomor-sromotnikowy',
    edible: false,
    edibleLabel: EDIBLE_DEADLY,
    warning: { native: 'ŚMIERTELNIE TRUJĄCY!', pl: 'NIGDY nie zbieraj — jeden grzyb wystarczy, żeby zatruć dorosłego' },
    season: { native: 'Lipiec–Październik', pl: 'w lasach liściastych, najczęściej pod dębami' },
    grows: { pl: 'Lasy liściaste, pod dębami', en: 'Broadleaf woods, under oaks' },
    funFact: { pl: 'Muchomor sromotnikowy bywa mylony z jadalną gąską albo pieczarką — ma zielonkawy kapelusz, biały pierścień i pochwę u podstawy. Dlatego grzyby zbieramy WYŁĄCZNIE z dorosłym.', en: 'The death cap is mistaken for edible mushrooms — never pick mushrooms without an adult.' },
    real: { imageKey: 'real_muchomor_sromotnikowy', caption: { pl: 'Muchomor sromotnikowy — nigdy nie zbieraj!', en: 'Death cap — never pick it' } },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// ROŚLINY I DRZEWA
// ─────────────────────────────────────────────────────────────────────────────

interface PlantInput {
  petId: ID;
  kind: LocalizedString;
  height: LocalizedString;
  grows: LocalizedString;
  /** „Rozpoznasz mnie": `native` = cecha do zapamiętania, `pl` = wyjaśnienie. */
  recognise: { native: string; pl: string };
  /** „Kiedy kwitnę": `native` = pora, `pl` = co wtedy widać. */
  season: { native: string; pl: string };
  funFact: LocalizedString;
  real: { imageKey: string; caption: LocalizedString };
}

function plant(input: PlantInput): EduEntry {
  return {
    facts: [
      { label: LABEL_KIND, value: input.kind },
      { label: LABEL_HEIGHT, value: input.height },
      { label: LABEL_GROWS, value: input.grows },
    ],
    phrases: [
      { labelPl: 'Rozpoznasz mnie', native: input.recognise.native, pl: input.recognise.pl, iconKey: ICON_VOICE },
      { labelPl: 'Moja pora', native: input.season.native, pl: input.season.pl, iconKey: ICON_INFO },
    ],
    funFact: input.funFact,
    landmark: { imageKey: input.real.imageKey, caption: input.real.caption },
  };
}

const TREE_LEAF: LocalizedString = { pl: 'Drzewo liściaste', en: 'Broadleaf tree' };
const TREE_CONIFER: LocalizedString = { pl: 'Drzewo iglaste', en: 'Conifer' };
const UNDERGROWTH: LocalizedString = { pl: 'Roślina runa leśnego', en: 'Forest-floor plant' };

const PLANTS: PlantInput[] = [
  {
    petId: 'pet-puszcza-grab',
    kind: TREE_LEAF,
    height: { pl: 'Do 25 m', en: 'Up to 25 m' },
    grows: { pl: 'Grądy — najczęstszy las puszczy', en: 'Oak-hornbeam woods' },
    recognise: { native: 'Pień jak napięty mięsień', pl: 'kora gładka i szara, pień pofalowany, nie okrągły' },
    season: { native: 'Liście do zimy', pl: 'młody grab trzyma suche liście aż do wiosny' },
    funFact: { pl: 'Drewno grabu jest tak twarde, że dawniej robiono z niego zęby do młyńskich kół i narzędzia.', en: 'Hornbeam wood is so hard it was used for mill-wheel cogs.' },
    real: { imageKey: 'real_grab', caption: { pl: 'Grab pospolity — pofalowany pień', en: 'Hornbeam with its fluted trunk' } },
  },
  {
    petId: 'pet-puszcza-sosna',
    kind: TREE_CONIFER,
    height: { pl: 'Do 40 m', en: 'Up to 40 m' },
    grows: { pl: 'Suche bory na piaskach', en: 'Dry sandy pine woods' },
    recognise: { native: 'Igły po DWIE', pl: 'igły rosną parami, a kora na górze jest pomarańczowa' },
    season: { native: 'Zielona cały rok', pl: 'zrzuca igły stopniowo, nigdy wszystkich naraz' },
    funFact: { pl: 'Sosna to najczęstsze drzewo w Polsce. Jej żywica pachnie lasem i potrafi zasklepić ranę w korze jak plaster.', en: 'The Scots pine is Poland’s most common tree; its resin seals wounds in the bark.' },
    real: { imageKey: 'real_sosna', caption: { pl: 'Sosna zwyczajna z rudą korą', en: 'Scots pine with orange bark' } },
  },
  {
    petId: 'pet-puszcza-brzoza',
    kind: TREE_LEAF,
    height: { pl: 'Do 30 m', en: 'Up to 30 m' },
    grows: { pl: 'Skraje lasu i wycinki', en: 'Forest edges and clearings' },
    recognise: { native: 'Biała kora', pl: 'jedyne białe drzewo w puszczy — nie da się pomylić' },
    season: { native: 'Wiosną płaczą', pl: 'na wiosnę z brzozy leci słodki sok' },
    funFact: { pl: 'Brzoza jest pionierem — pierwsza wchodzi na puste miejsca po pożarze czy wycince i przygotowuje glebę dla innych drzew.', en: 'Birch is a pioneer tree, first to colonise open ground.' },
    real: { imageKey: 'real_brzoza', caption: { pl: 'Brzoza brodawkowata o białej korze', en: 'Silver birch with white bark' } },
  },
  {
    petId: 'pet-puszcza-zawilec',
    kind: UNDERGROWTH,
    height: { pl: 'Do 25 cm', en: 'Up to 25 cm' },
    grows: { pl: 'Dno lasu, wielkie białe dywany', en: 'Forest floor, in white carpets' },
    recognise: { native: 'Biała gwiazdka', pl: 'sześć białych płatków tuż nad ściółką' },
    season: { native: 'Marzec–Maj', pl: 'kwitnie ZANIM drzewa wypuszczą liście' },
    funFact: { pl: 'Zawilec śpieszy się: kwitnie wczesną wiosną, póki przez gołe gałęzie dociera do dna lasu słońce. Latem znika bez śladu.', en: 'Wood anemone blooms before the trees leaf out, then disappears.' },
    real: { imageKey: 'real_zawilec', caption: { pl: 'Dywan zawilców gajowych wiosną', en: 'Carpet of wood anemones in spring' } },
  },
  {
    petId: 'pet-puszcza-olsza',
    kind: TREE_LEAF,
    height: { pl: 'Do 30 m', en: 'Up to 30 m' },
    grows: { pl: 'Olsy — mokre, podtopione lasy', en: 'Alder carrs — wet woodland' },
    recognise: { native: 'Małe „szyszeczki"', pl: 'jedyne liściaste drzewo z drewnianymi szyszkami' },
    season: { native: 'Szyszeczki zimą', pl: 'wiszą na gałęziach przez całą zimę' },
    funFact: { pl: 'Drewno olszy nie gnije pod wodą — na olszowych palach stoi część Wenecji i dawne dworki na mokradłach.', en: 'Alder wood does not rot under water; parts of Venice stand on alder piles.' },
    real: { imageKey: 'real_olsza', caption: { pl: 'Olsza czarna nad wodą', en: 'Black alder by the water' } },
  },
  {
    petId: 'pet-puszcza-lipa',
    kind: TREE_LEAF,
    height: { pl: 'Do 30 m', en: 'Up to 30 m' },
    grows: { pl: 'Grądy i przydroża', en: 'Mixed woods and roadsides' },
    recognise: { native: 'Liść jak serce', pl: 'liście lipy mają kształt serduszka' },
    season: { native: 'Czerwiec–Lipiec', pl: 'wtedy pachnie tak, że słychać brzęczenie z daleka' },
    funFact: { pl: 'Kwitnąca lipa potrafi „szumieć" od tysięcy pszczół naraz. Z jej kwiatów robi się herbatę na przeziębienie.', en: 'A blooming lime tree hums with thousands of bees.' },
    real: { imageKey: 'real_lipa', caption: { pl: 'Kwitnąca lipa drobnolistna', en: 'Small-leaved lime in bloom' } },
  },
  {
    petId: 'pet-puszcza-konwalia',
    kind: UNDERGROWTH,
    height: { pl: 'Do 25 cm', en: 'Up to 25 cm' },
    grows: { pl: 'Cieniste grądy i dąbrowy', en: 'Shady mixed and oak woods' },
    recognise: { native: 'Białe dzwoneczki', pl: 'rządek białych dzwonków między dwoma szerokimi liśćmi' },
    season: { native: 'Maj', pl: 'wtedy pachnie na cały las' },
    funFact: { pl: 'Konwalia pięknie pachnie, ale jest TRUJĄCA — zwłaszcza czerwone jagódki, które robią się po kwiatach. Nie wkładaj do buzi.', en: 'Lily of the valley smells lovely but is poisonous — especially its red berries.' },
    real: { imageKey: 'real_konwalia', caption: { pl: 'Konwalia majowa — piękna, ale trująca', en: 'Lily of the valley — beautiful but poisonous' } },
  },
  {
    petId: 'pet-puszcza-jesion',
    kind: TREE_LEAF,
    height: { pl: 'Do 40 m', en: 'Up to 40 m' },
    grows: { pl: 'Żyzne, wilgotne łęgi', en: 'Rich, moist riverside woods' },
    recognise: { native: 'Czarne pąki', pl: 'zimą pąki jesionu są aksamitnie czarne — nikt inny takich nie ma' },
    season: { native: 'Ostatni wypuszcza liście', pl: 'zielenieje najpóźniej z całej puszczy' },
    funFact: { pl: 'Jesion ma najtwardsze i najbardziej sprężyste drewno w polskim lesie — robi się z niego trzonki narzędzi i narty.', en: 'Ash has the springiest wood in Polish forests; tool handles are made from it.' },
    real: { imageKey: 'real_jesion', caption: { pl: 'Jesion wyniosły z czarnymi pąkami', en: 'European ash with black buds' } },
  },
  {
    petId: 'pet-puszcza-paproc',
    kind: UNDERGROWTH,
    height: { pl: 'Do 1,5 m', en: 'Up to 1.5 m' },
    grows: { pl: 'Cieniste, wilgotne ostępy', en: 'Shady, damp forest interior' },
    recognise: { native: 'Liść jak pióro', pl: 'wielkie pierzaste liście wyrastające z jednej kępy' },
    season: { native: 'Wiosną rozwija ślimaczki', pl: 'młody liść jest zwinięty jak pastorał' },
    funFact: { pl: 'Paprocie rosły na Ziemi już za czasów dinozaurów i nie mają kwiatów — rozmnażają się zarodnikami spod liści. „Kwiat paproci" to legenda!', en: 'Ferns are older than dinosaurs and have no flowers — the “fern flower” is a legend.' },
    real: { imageKey: 'real_paproc', caption: { pl: 'Narecznica samcza w cieniu lasu', en: 'Male fern in forest shade' } },
  },
  {
    petId: 'pet-puszcza-dab',
    kind: TREE_LEAF,
    height: { pl: 'Do 40 m, żyje 500+ lat', en: 'Up to 40 m, lives 500+ years' },
    grows: { pl: 'Serce puszczy — rezerwat Lipówka', en: 'The heart of the forest' },
    recognise: { native: 'Liść z falbankami', pl: 'wcinany liść i żołędzie na długich szypułkach' },
    season: { native: 'Żołędzie we wrześniu', pl: 'wtedy pod dębem zbierają się dziki, sójki i wiewiórki' },
    funFact: { pl: 'Dąb szypułkowy to król Puszczy Niepołomickiej. W rezerwacie Lipówka rosną dęby starsze niż 400 lat, a na jednym dębie może żyć ponad 500 gatunków zwierząt.', en: 'The English oak is the king of Niepołomice Forest; a single oak can host 500+ species.' },
    real: { imageKey: 'real_dab', caption: { pl: 'Pomnikowy dąb szypułkowy w rezerwacie Lipówka', en: 'Monumental English oak in the Lipówka reserve' } },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// REJESTR
// ─────────────────────────────────────────────────────────────────────────────

/** Wpisy edukacyjne Puszczy Niepołomickiej, kluczowane po `petId`. */
export const PUSZCZA_EDU: Record<ID, EduEntry> = {
  ...Object.fromEntries(ANIMALS.map((a) => [a.petId, animal(a)])),
  ...Object.fromEntries(MUSHROOMS.map((m) => [m.petId, mushroom(m)])),
  ...Object.fromEntries(PLANTS.map((p) => [p.petId, plant(p)])),
  'pet-puszcza-zubr': ZUBR,
};

/** Nazwa puszczy — trzymana tu, żeby opisy w UI nie powtarzały literału. */
export const PUSZCZA_NAME = FOREST;
